import {
  Box,
  Collapse,
  Drawer as MuiDrawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
  Toolbar,
  useMediaQuery,
  useTheme,
  Tooltip,
  SxProps,
  Theme,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Article,
  Assessment,
  ChevronLeft,
  ChevronRight,
  ChromeReaderMode,
  DonutLarge,
  ExpandLess,
  ExpandMore,
  Moving,
  Toll,
  ViewQuilt,
} from '@mui/icons-material';
import { useHistory, useLocation } from 'react-router';
import Colors from '@src/app/config/colorConfig';
import { MouseEvent, ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  assetAllocationUrl,
  allTransactionsUrl,
  beneficiariesUrl,
  benefitStatementUrl,
  contributionsUrl,
  documentsUrl,
  holdingsUrl,
  homeUrl,
  insuranceUrl,
  pensionUrl,
  performanceUrl,
} from '@src/app/utils/UrlBuilder';
import { useAppSelector } from '@src/app/hooks/store';
import { SupersimplifierStore, selectSupersimplifier } from '@src/app/store/reducers/supersimplifier';
import { useAppContext } from '@src/app/hooks/useAppContext';
import { selectPortfolios } from '@src/app/store/reducers/portfolios/selectors';

const DRAWER_WIDTH = 285;

interface DrawerRouteList {
  routes: DrawerRoute[];
  subMenu: boolean;
}

interface DrawerRoute {
  icon?: ReactNode;
  label: string;
  path?: string;
  children?: DrawerRoute[];
  sub?: boolean;
}

const defaultRoutes: DrawerRoute[] = [
  { icon: <ViewQuilt />, label: 'Dashboard', path: homeUrl() },
  { icon: <Assessment />, label: 'Holdings', path: holdingsUrl() },
  { icon: <DonutLarge />, label: 'Asset Allocation', path: assetAllocationUrl() },
  { icon: <Moving />, label: 'Performance', path: performanceUrl() },
  { icon: <ChromeReaderMode />, label: 'Transactions', path: allTransactionsUrl() },
  { icon: <Article />, label: 'Documents', path: documentsUrl() },
];

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2),
  ...theme.mixins.toolbar,
}));

const DrawerList = (props: DrawerRouteList) => {
  const { drawerExpanded } = useAppContext();
  const sx: SxProps<Theme> = !drawerExpanded ? { display: 'flex', flexDirection: 'column', alignItems: 'center' } : {};
  return (
    <List sx={sx}>
      {props.routes.map((route, index) => (
        <DrawerItem sub={props.subMenu} key={index} {...route} />
      ))}
    </List>
  );
};

const DrawerItem = (props: DrawerRoute) => {
  const { drawerExpanded, setDrawerExpanded, transitioningMenu } = useAppContext();
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isMd = useMediaQuery(theme.breakpoints.down('md'));
  const selected = props.path && location.pathname.includes(props.path);
  const sidePadding = props.sub ? 2 : 1;

  const isSubMenuOpened = (): boolean => {
    if (!props.children) {
      return false;
    }
    return props.children.findIndex((child) => child.path && location.pathname.includes(child.path)) !== -1;
  };

  const [expanded, setExpanded] = useState(isSubMenuOpened());

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleListButtonClick = () => {
    if (props.path) {
      if (isMd) {
        setDrawerExpanded(false);
      }
      history.push(props.path);
    } else {
      setExpanded(!expanded);
    }
  };

  const handleIconButtonClick = (target: MouseEvent<HTMLElement>) => {
    if (props.path) {
      history.push(props.path);
    } else {
      setExpanded(!expanded);
      setAnchorEl(target.currentTarget);
    }
  };

  const handleMenuRouteClick = (route: DrawerRoute) => {
    if (route.path) {
      history.push(route.path);
    }
    handleClose();
  };

  if (!drawerExpanded) {
    return (
      <>
        <Box pb={1} height="56px" justifyContent="center" display="flex">
          <Tooltip title={props.label}>
            <IconButton
              onClick={(e) => handleIconButtonClick(e)}
              disabled={props.path === '#'}
              sx={{ color: selected ? 'black' : '#AEBBD2', width: '48px' }}
            >
              {props.icon}
            </IconButton>
          </Tooltip>
        </Box>
        {props.children && (
          <Menu onClose={handleClose} anchorEl={anchorEl} open={Boolean(anchorEl)}>
            {props.children?.map((child, index) => (
              <MenuItem onClick={() => handleMenuRouteClick(child)} key={index}>
                {child.label}
              </MenuItem>
            ))}
          </Menu>
        )}
      </>
    );
  }

  return (
    <Box pl={sidePadding} pr={sidePadding} pb={props.sub ? 0 : 1} sx={{ overflowX: 'hidden' }}>
      <ListItemButton disabled={props.path === '#'} onClick={handleListButtonClick} sx={{ borderRadius: '12px', height: '48px' }}>
        {props.icon && <ListItemIcon sx={{ color: selected ? 'black' : '#AEBBD2' }}>{props.icon}</ListItemIcon>}
        {!transitioningMenu && (
          <>
            <ListItemText
              primaryTypographyProps={{
                textTransform: 'uppercase',
                color: selected ? 'black' : Colors.gray600,
                fontWeight: 'bold',
                textOverflow: 'clip',
                fontSize: props.sub ? 14 : 16,
              }}
              primary={props.label}
            />
            {props.children && <>{expanded ? <ExpandLess sx={{ color: '#AEBBD2' }} /> : <ExpandMore sx={{ color: '#AEBBD2' }} />}</>}
          </>
        )}
      </ListItemButton>

      {!transitioningMenu && props.children && (
        <Box pl={6}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <DrawerList subMenu={true} routes={props.children} />
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

const Drawer = ({ children }: { children: ReactNode }): JSX.Element => {
  const { drawerExpanded, setDrawerExpanded, setTransitioningMenu } = useAppContext();
  const { logoUrl } = useAppSelector(selectPortfolios);
  const superSimplifierStore: SupersimplifierStore = useAppSelector(selectSupersimplifier);
  const [scrollY, setScrollY] = useState<number>(window.scrollY);

  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down('md'));
  const isXs = useMediaQuery(theme.breakpoints.down('xs'));

  const drawerWidth = isXs ? DRAWER_WIDTH - 15 : isMd ? DRAWER_WIDTH : drawerExpanded ? DRAWER_WIDTH : 74;

  const isOpen = !isMd || drawerExpanded;

  const routes: DrawerRoute[] = [...defaultRoutes];

  let isPensionAccount = false;
  let isSuperAccount = false;

  const accounts = superSimplifierStore.superSimplifierAccounts;

  if (accounts?.data?.length) {
    const subMenuSuper: DrawerRoute = {
      label: 'Super Simplifier',
      icon: <Toll />,
      children: [],
    };

    if (accounts.data[0].accountNumber && accounts.data[0].name) {
      isPensionAccount = !!accounts.data.find((i) => i.type === 'Pension') || false;
      isSuperAccount = !!accounts.data.find((i) => i.type === 'Super') || false;
    }
    subMenuSuper.children?.push({
      label: 'Benefit Statement',
      path: benefitStatementUrl(),
    });
    subMenuSuper.children?.push({
      label: 'Beneficiaries',
      path: beneficiariesUrl(),
    });
    if (isPensionAccount) {
      subMenuSuper.children?.push({
        label: 'Pension Details',
        path: pensionUrl(),
      });
    }
    subMenuSuper.children?.push({
      label: 'Insurance',
      path: insuranceUrl(),
    });
    if (isSuperAccount) {
      subMenuSuper.children?.push({
        label: 'Contributions',
        path: contributionsUrl(),
      });
    }

    routes.push(subMenuSuper);
  }

  const toggle = () => {
    if (isMd) {
      setDrawerExpanded(!drawerExpanded);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getMarginTop = () => {
    if (scrollY < 40) return theme.spacing(9);
    if (scrollY < 55) return theme.spacing(3);
    if (scrollY < 70) return theme.spacing(5);
    if (scrollY >= 70) return theme.spacing(0);
    return theme.spacing(9);
  };

  return (
    <Box component="div" display={!isMd ? 'flex' : ''}>
      <MuiDrawer
        variant={isMd ? 'temporary' : 'permanent'}
        open={isOpen}
        onClose={toggle}
        onTransitionEnd={() => {
          setTransitioningMenu(false);
        }}
        sx={{
          flexShrink: 0,
          width: drawerWidth,
          overflowX: 'hidden',

          [`& .MuiDrawer-paper`]: {
            backgroundColor: '#F8FAFB',
            border: !isMd ? 'none' : '',
            width: drawerWidth,
            overflowX: 'hidden',
            transition: !isOpen
              ? theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.leavingScreen,
                })
              : theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
          },
        }}
      >
        <>
          {isMd && (
            <DrawerHeader>
              <Box display="flex" flex={1}>
                {logoUrl && (
                  <Link to={homeUrl()} color="inherit">
                    <img src={logoUrl} alt="Logo" width="164" />
                  </Link>
                )}
              </Box>

              <IconButton onClick={() => setDrawerExpanded(!drawerExpanded)}>{drawerExpanded ? <ChevronLeft /> : <ChevronRight />}</IconButton>
            </DrawerHeader>
          )}
          {scrollY < 110 && !isMd && <Toolbar />}
          <Box sx={{ overflow: 'auto' }} display="flex" flexDirection="column" mt={getMarginTop()}>
            <DrawerList subMenu={false} routes={routes} />
          </Box>
        </>
      </MuiDrawer>
      <Box display="flex" flexDirection="column" flex={1}>
        {children}
      </Box>
    </Box>
  );
};

export default Drawer;
